/**
 * @konstellation/ui - Related Articles Component
 * 
 * Displays related articles for SEO internal linking.
 * Used in blog articles to improve site navigation and reduce orphan pages.
 * 
 * Uses React.memo for performance optimization
 */

import { memo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export interface RelatedArticle {
  slug: string;
  title: string;
  category: string;
  excerpt?: string;
}

interface RelatedArticlesProps {
  articles: RelatedArticle[];
  className?: string;
}

/**
 * Related Articles Section
 * 
 * Displays a grid of related article cards for SEO internal linking.
 * Should be placed at the end of blog articles, before the CTA.
 * 
 * @example
 * ```tsx
 * import { RelatedArticles } from '@konstellation/ui';
 * 
 * <RelatedArticles 
 *   articles={[
 *     { slug: 'article-1', title: 'Article 1', category: 'guide' },
 *     { slug: 'article-2', title: 'Article 2', category: 'comparatif' },
 *   ]}
 * />
 * ```
 */

// Memoized card component for individual articles
const RelatedArticleCard = memo(function RelatedArticleCard({ article }: { article: RelatedArticle }) {
  return (
    <Link
      to={`/blog/${article.slug}/`}
      className="p-4 bg-muted/30 rounded-lg border border-border hover:border-primary/50 transition-colors group"
    >
      <span className="text-xs text-muted-foreground uppercase tracking-wide">
        {article.category}
      </span>
      <p className="font-medium text-foreground group-hover:text-primary transition-colors line-clamp-2">
        {article.title}
      </p>
      {article.excerpt && (
        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
          {article.excerpt}
        </p>
      )}
    </Link>
  );
});

export const RelatedArticles = memo(function RelatedArticles({ articles, className = '' }: RelatedArticlesProps) {
  if (!articles || articles.length === 0) {
    return null;
  }

  return (
    <section className={`mt-12 pt-8 border-t border-border ${className}`}>
      <h2 className="text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
        <ArrowRight className="w-5 h-5 text-primary" />
        Articles connexes
      </h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {articles.map((article) => (
          <RelatedArticleCard key={article.slug} article={article} />
        ))}
      </div>
    </section>
  );
});

export default RelatedArticles;

